import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, MessageCircleOff, BookX, Timer, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayToetsAvatar } from '../../mascot';
import { DOMAIN_META } from '../utils/assessmentDomainConfig';
import type { AssessmentDomainId } from '../types';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
interface AssessmentIntroScreenProps {
  domainId: AssessmentDomainId;
  questionCount: number;
  onStart: () => void;
  onCancel: () => void;
}
export function AssessmentIntroScreen({
  domainId,
  questionCount,
  onStart,
  onCancel
}: AssessmentIntroScreenProps) {
  const meta = DOMAIN_META[domainId];
  const minutesPerQuestion = domainId === 'rekenen' ? 1.4 : 1.2;
  const estimatedMinutes = Math.round(questionCount * minutesPerQuestion);
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-slate-800" data-id="element-672" data-mp="1k1z633gtbixp">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md text-center" data-id="element-673" data-mp="2ag0ngbgi00l">
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        bounce: 0.4
      }} className="flex justify-center mb-6" data-id="element-674" data-mp="3pgyvv7igj8q9">
          <DjayToetsAvatar domain={domainId} size="lg" animate data-id="element-675" data-mp="c95g72fvr8do" />
        </motion.div>

        <h1 className="text-3xl font-black text-slate-900 mb-2 tracking-tight" data-id="element-676" data-mp="h6x83wgiyuhv">
          {meta.label}
        </h1>
        <p className="text-slate-500 text-sm font-medium mb-8" data-id="element-677" data-mp="14w01g1kiubs7">Toets</p>

        <div className="flex items-center justify-center gap-6 text-slate-600 font-medium mb-10" data-id="element-678" data-mp="19nhtg04kh211">
          <div className="flex items-center" data-id="element-679" data-mp="1jjgjacjd0q6x">
            <CheckCircle2 className="w-4 h-4 mr-2 text-slate-400" data-id="element-680" data-mp="1jj876mv29ows" />
            {questionCount} vragen
          </div>
          <div className="flex items-center" data-id="element-681" data-mp="1eikfdyz0d41i">
            <Clock className="w-4 h-4 mr-2 text-slate-400" data-id="element-682" data-mp="19krd3h0tsu6i" />~{estimatedMinutes}{' '}
            minuten
          </div>
        </div>

        <div className="space-y-4 mb-10 text-left inline-block" data-id="element-683" data-mp="14lxvbceiah0j">
          <div className="flex items-center gap-4" data-id="element-684" data-mp="10l47sk4z7486">
            <MessageCircleOff className="w-5 h-5 text-slate-400 shrink-0" data-id="element-685" data-mp="10myspyg8ywc9" />
            <p className="text-slate-600 font-medium" data-id="element-686" data-mp="vsky3y9est8n">
              Geen feedback na elke vraag
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-687" data-mp="qsq3pkqfdliu">
            <BookX className="w-5 h-5 text-slate-400 shrink-0" data-id="element-688" data-mp="lx15bkq56ao4" />
            <p className="text-slate-600 font-medium" data-id="element-689" data-mp="gwn34jb81uy0">
              Geen spiekbriefje of hulp
            </p>
          </div>
          <div className="flex items-center gap-4" data-id="element-690" data-mp="cwcnsg1jhs6h">
            <Timer className="w-5 h-5 text-slate-400 shrink-0" data-id="element-691" data-mp="cxnrygywd1y4" />
            <p className="text-slate-600 font-medium" data-id="element-692" data-mp="2oi3p5v7q2bc5">Neem rustig de tijd</p>
          </div>
        </div>

        <Button onClick={onStart} size="xl" fullWidth className="bg-orange-500 text-white hover:bg-orange-600 shadow-xl rounded-2xl text-fluid-lg font-bold mb-6" data-id="element-693" data-mp="2sino94buvp0x">
          <span className="mr-2" data-id="element-694" data-mp="32gbsckvcq8vr">Ik ben er klaar voor</span>
          <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-695" data-mp="24vp8y9zh2n4l" />
        </Button>

        <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600 font-medium transition-colors" data-id="element-696" data-mp="29t7j23p878e6">
          Annuleren en terug
        </button>
      </motion.div>
    </div>;
}